import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PropertyResponse, PropertyDocumentResponse, PageResponse, AdminUpdatePropertyRequest } from '../models';

@Injectable({ providedIn: 'root' })
export class PropertyAdminService {
  private http = inject(HttpClient);
  private base = `${environment.apis.property}/admin`;

  getAllProperties(page = 0, size = 20, status?: string): Observable<PageResponse<PropertyResponse>> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<PageResponse<PropertyResponse>>(`${this.base}/properties`, { params });
  }

  getPendingProperties(page = 0, size = 20): Observable<PageResponse<PropertyResponse>> {
    return this.getAllProperties(page, size, 'PENDING_REVIEW');
  }

  approveProperty(propertyId: string): Observable<PropertyResponse> {
    return this.http.patch<PropertyResponse>(`${this.base}/properties/${propertyId}/approve`, {});
  }

  rejectProperty(propertyId: string, reason: string): Observable<PropertyResponse> {
    return this.http.patch<PropertyResponse>(`${this.base}/properties/${propertyId}/reject`, { reason });
  }

  updateProperty(propertyId: string, changes: AdminUpdatePropertyRequest): Observable<PropertyResponse> {
    return this.http.patch<PropertyResponse>(`${this.base}/properties/${propertyId}`, changes);
  }

  /** Transitions manuelles : PUBLISHED → TAKEN | PENDING_REVIEW, TAKEN → PUBLISHED, REJECTED → PENDING_REVIEW. */
  changeStatus(propertyId: string, status: string): Observable<PropertyResponse> {
    return this.http.patch<PropertyResponse>(`${this.base}/properties/${propertyId}/status`, { status });
  }

  /** Suppression réversible (soft delete) : le bien part dans la corbeille. */
  deleteProperty(propertyId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/properties/${propertyId}`);
  }

  restoreProperty(propertyId: string): Observable<PropertyResponse> {
    return this.http.post<PropertyResponse>(`${this.base}/properties/${propertyId}/restore`, {});
  }

  getDeletedProperties(page = 0, size = 20): Observable<PageResponse<PropertyResponse>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PageResponse<PropertyResponse>>(`${this.base}/properties/deleted`, { params });
  }

  getProperty(propertyId: string): Observable<PropertyResponse> {
    return this.http.get<PropertyResponse>(`${environment.apis.property}/properties/${propertyId}`);
  }

  getPropertyDocuments(propertyId: string): Observable<PropertyDocumentResponse[]> {
    return this.http.get<PropertyDocumentResponse[]>(`${this.base}/properties/${propertyId}/documents`);
  }

  validateDocument(docId: string): Observable<PropertyDocumentResponse> {
    return this.http.patch<PropertyDocumentResponse>(`${this.base}/documents/${docId}/validate`, {});
  }

  rejectDocument(docId: string, reason: string): Observable<PropertyDocumentResponse> {
    return this.http.patch<PropertyDocumentResponse>(`${this.base}/documents/${docId}/reject`, { reason });
  }

  getDocumentUrl(docId: string): Observable<string> {
    return this.http.get(`${this.base}/properties/${docId}/documents/url`, { responseType: 'text' });
  }

  getPhotoUrl(docId: string): Observable<string> {
    return this.http.get(`${environment.apis.document}/admin/documents/${docId}/url`, { responseType: 'text' });
  }
}
